import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { notificationsAPI } from '../services/api';
import {
  enableNotificationDevice,
  isIosDevice,
  isNotificationSupported,
  isStandalone,
} from '../utils/notificationSubscription';

export default function NotificationSettings({ hideHeading = false }) {
  const supported = useMemo(() => isNotificationSupported(), []);
  const iosNeedsInstall = useMemo(
    () => supported && isIosDevice() && !isStandalone(),
    [supported]
  );
  const [config, setConfig] = useState(null);
  const [preferences, setPreferences] = useState({
    enabled: false,
    memberChannelMessage: false,
    activeDeviceCount: 0,
  });
  const [subscribedHere, setSubscribedHere] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [status, setStatus] = useState({ severity: 'info', text: '' });
  const politeStatusRef = useRef(null);
  const assertiveStatusRef = useRef(null);

  useEffect(() => {
    const isUrgent = status.severity === 'error' || status.severity === 'warning';
    if (politeStatusRef.current) {
      politeStatusRef.current.textContent = isUrgent ? '' : status.text;
    }
    if (assertiveStatusRef.current) {
      assertiveStatusRef.current.textContent = isUrgent ? status.text : '';
    }
  }, [status]);

  useEffect(() => {
    let cancelled = false;
    if (!supported) return undefined;
    Promise.all([
      notificationsAPI.getConfig(),
      notificationsAPI.getPreferences(),
      navigator.serviceWorker.getRegistration().then(
        registration => registration?.pushManager.getSubscription() || null
      ),
    ]).then(([configResponse, preferenceResponse, subscription]) => {
      if (cancelled) return;
      setConfig(configResponse.data?.data || null);
      setPreferences(current => ({
        ...current,
        ...(preferenceResponse.data?.data || {}),
      }));
      setSubscribedHere(Boolean(subscription));
      setLoaded(true);
    }).catch((error) => {
      if (!cancelled) {
        setStatus({
          severity: 'error',
          text: error.response?.data?.error
            || 'Notification settings failed to load. Close Settings and try again.',
        });
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const enableDevice = async () => {
    if (!supported || iosNeedsInstall || !config?.publicKey) return;
    setBusy(true);
    setStatus({ severity: 'info', text: '' });
    try {
      const result = await enableNotificationDevice(config.publicKey);
      if (result.permission !== 'granted') {
        setStatus({
          severity: 'warning',
          text: result.permission === 'denied'
            ? 'Notifications are blocked. Open this site in your browser settings and change Notifications to Allow.'
            : 'You did not grant permission. Select Enable on this device to try again.',
        });
        return;
      }
      setPreferences(current => ({
        ...current,
        ...(result.response.data?.data?.preferences || {}),
        activeDeviceCount: Math.max(1, current.activeDeviceCount + (subscribedHere ? 0 : 1)),
      }));
      setSubscribedHere(true);
      setStatus({ severity: 'success', text: 'Notifications are enabled on this device.' });
    } catch (error) {
      setStatus({
        severity: 'error',
        text: error.response?.data?.error || 'Could not enable notifications on this device.',
      });
    } finally {
      setBusy(false);
    }
  };

  const updatePreferences = async (next) => {
    setBusy(true);
    setStatus({ severity: 'info', text: '' });
    try {
      const response = await notificationsAPI.updatePreferences({
        enabled: next.enabled,
        memberChannelMessage: next.memberChannelMessage,
      });
      setPreferences(current => ({
        ...current,
        ...(response.data?.data || next),
      }));
      setStatus({ severity: 'success', text: 'Notification preferences saved.' });
    } catch (error) {
      setStatus({
        severity: 'error',
        text: error.response?.data?.error || 'Could not save notification preferences.',
      });
    } finally {
      setBusy(false);
    }
  };

  const removeDevice = async () => {
    setBusy(true);
    setStatus({ severity: 'info', text: '' });
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        setSubscribedHere(false);
        setStatus({ severity: 'info', text: 'This device is not subscribed.' });
        return;
      }
      const [serverResult, browserResult] = await Promise.allSettled([
        notificationsAPI.unsubscribeCurrent(subscription.endpoint),
        subscription.unsubscribe(),
      ]);
      const serverRemoved = serverResult.status === 'fulfilled';
      const browserRemoved = browserResult.status === 'fulfilled' && browserResult.value === true;
      if (!serverRemoved && !browserRemoved) {
        throw serverResult.status === 'rejected'
          ? serverResult.reason
          : new Error('Browser did not remove the subscription');
      }
      setSubscribedHere(false);
      setPreferences(current => ({
        ...current,
        activeDeviceCount: serverRemoved
          ? Math.max(0, current.activeDeviceCount - 1)
          : current.activeDeviceCount,
      }));
      setConfirmRemove(false);
      setStatus({
        severity: serverRemoved && browserRemoved ? 'success' : 'warning',
        text: serverRemoved && browserRemoved
          ? 'Notifications stopped on this device.'
          : 'Notifications stopped here, but complete cleanup could not be confirmed.',
      });
    } catch (error) {
      setStatus({
        severity: 'error',
        text: error.response?.data?.error || 'Could not remove this device.',
      });
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <Alert severity="info">
        Web notifications are not supported in this browser. Open EliteEdge in Chrome, Edge, or Safari.
      </Alert>
    );
  }
  if (iosNeedsInstall) {
    return (
      <Alert severity="info">
        To enable notifications on iPhone or iPad:
        <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          <li>Open EliteEdge in Safari.</li>
          <li>Tap the Share icon, shown as a square with an upward arrow.</li>
          <li>Select Add to Home Screen.</li>
          <li>Open EliteEdge from the new Home Screen icon and return here.</li>
        </ol>
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        component="span"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        ref={politeStatusRef}
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          p: 0,
          m: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {''}
      </Box>
      <Box
        component="span"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        ref={assertiveStatusRef}
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          p: 0,
          m: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {''}
      </Box>
      {!hideHeading && (
        <Typography
          component="h3"
          sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 0.5 }}
        >
          App notifications
        </Typography>
      )}
      <Typography sx={{ color: 'var(--text-dim)', fontSize: 13, mb: 1.5 }}>
        Receive a private alert when a new team message is available. The alert only identifies the message type; it never shows message or player details.
      </Typography>

      {status.text && (
        <Alert severity={status.severity} sx={{ mb: 1.5 }}>
          {status.text}
        </Alert>
      )}

      {!subscribedHere ? (
        <Button
          type="button"
          variant="contained"
          onClick={enableDevice}
          disabled={busy || !loaded || !config?.publicKey}
          sx={{ minHeight: 44, textTransform: 'none' }}
        >
          {!loaded ? 'Loading...' : busy ? 'Enabling...' : 'Enable on this device'}
        </Button>
      ) : (
        <>
          <Box role="group" aria-label="Notification preferences">
            <FormControlLabel
              sx={{ minHeight: 44 }}
              control={(
                <Switch
                  checked={preferences.enabled}
                  disabled={busy}
                  onChange={event => updatePreferences({
                    ...preferences,
                    enabled: event.target.checked,
                  })}
                />
              )}
              label="Notifications enabled on all devices"
            />
            <FormControlLabel
              sx={{ minHeight: 44 }}
              control={(
                <Switch
                  checked={preferences.memberChannelMessage}
                  disabled={busy || !preferences.enabled}
                  inputProps={{
                    'aria-describedby': !preferences.enabled
                      ? 'member-message-notification-disabled-help'
                      : undefined,
                  }}
                  onChange={event => updatePreferences({
                    ...preferences,
                    memberChannelMessage: event.target.checked,
                  })}
                />
              )}
              label="New team messages"
            />
            {!preferences.enabled && (
              <Typography
                id="member-message-notification-disabled-help"
                sx={{ color: 'var(--text-dim)', fontSize: 12, mb: 1 }}
              >
                Turn on notifications for all devices before selecting message alerts.
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
            {!confirmRemove ? (
              <Button
                type="button"
                variant="outlined"
                color="error"
                onClick={() => setConfirmRemove(true)}
                disabled={busy}
                sx={{ minHeight: 44, textTransform: 'none' }}
              >
                Stop notifications on this device
              </Button>
            ) : (
              <>
                <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  Stop push alerts on this device?
                </Typography>
                <Button
                  type="button"
                  variant="contained"
                  color="error"
                  onClick={removeDevice}
                  disabled={busy}
                  sx={{ minHeight: 44, textTransform: 'none' }}
                >
                  {busy ? 'Stopping...' : 'Confirm stop'}
                </Button>
                <Button
                  type="button"
                  variant="text"
                  onClick={() => setConfirmRemove(false)}
                  disabled={busy}
                  sx={{ minHeight: 44, textTransform: 'none' }}
                >
                  Cancel
                </Button>
              </>
            )}
            <Typography sx={{ color: 'var(--text-dim)', fontSize: 12 }}>
              Devices receiving alerts: {preferences.activeDeviceCount}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
